// Q35 — "Guardar / Partilhar projeto": captures the customer's contact (a lead,
// RGPD-consented) and returns a shareable resume link. Mounted in the division
// grid header so the customer can save progress at any point.

import { useState } from 'react';
import { saveProject, resumeLink, type SaveCustomer } from '../lib/projectApi';

export function SaveProjectButton() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setError(null); setLink(null); setCopied(false);
  }

  async function handleSave() {
    setError(null);
    if (!name.trim() || !email.trim()) { setError('Indica nome e email.'); return; }
    if (!consent) { setError('É preciso autorizar o contacto para guardarmos o projeto.'); return; }
    setSaving(true);
    try {
      const customer: SaveCustomer = { name: name.trim(), email: email.trim(), phone: phone.trim() || undefined };
      const { id, token } = await saveProject(customer);
      setLink(resumeLink(id, token));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function copy() {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        className="rounded-smartify border border-smartify-line px-3 py-1.5 text-xs font-medium text-smartify-ink hover:border-smartify-brand"
      >
        💾 Guardar / Partilhar
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          role="dialog" aria-modal="true" aria-label="Guardar projeto"
          onClick={() => setOpen(false)}
        >
          <div className="w-full max-w-md rounded-t-2xl bg-white p-5 sm:rounded-smartify" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-smartify-ink">Guardar e partilhar projeto</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Fechar" className="p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink">✕</button>
            </div>

            {link ? (
              <div className="space-y-3">
                <p className="text-sm text-smartify-muted">Projeto guardado ✅ Usa este link para retomar em qualquer dispositivo (a configuração é mantida; as fotos ficam no dispositivo onde foram tiradas).</p>
                <div className="flex gap-2">
                  <input readOnly value={link} className="min-w-0 flex-1 rounded-smartify border border-smartify-line px-2 py-2 text-xs text-smartify-ink" onFocus={(e) => e.currentTarget.select()} />
                  <button type="button" onClick={copy} className="shrink-0 rounded-smartify bg-smartify-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90">{copied ? 'Copiado!' : 'Copiar'}</button>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="w-full rounded-smartify border border-smartify-line px-4 py-2.5 text-sm font-medium text-smartify-ink hover:border-smartify-brand">Concluir</button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-smartify-muted">Guarda o teu projeto e recebe um link para continuar mais tarde. A nossa equipa pode ajudar-te a finalizar o orçamento.</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome *" className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm focus:border-smartify-brand focus:outline-none" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email *" className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm focus:border-smartify-brand focus:outline-none" />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Telefone (opcional)" className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm focus:border-smartify-brand focus:outline-none" />
                <label className="flex items-start gap-2 text-xs text-smartify-muted">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                  <span>Autorizo a Smartify a guardar estes dados e contactar-me sobre o meu projeto (RGPD).</span>
                </label>
                {error && <p className="rounded-smartify border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
                <button type="button" disabled={saving} onClick={handleSave} className="w-full rounded-smartify bg-smartify-brand px-4 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">
                  {saving ? 'A guardar…' : 'Guardar e obter link'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
